import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";
import { Dayjs } from "dayjs";

import {
  getProjectTaskById,
  getProjectTaskSubtasks,
  createProjectTask,
  getProjectById,
  getProjectMembers,
  updateProjectTask,
} from "../services/projectService";

import {
  ProjectTaskDetail as ProjectTaskDetailType,
  Attachment,
  User,
  ProjectTask,
  Task,
  ProjectMember,
} from "../types";
import { ItemWithSource } from "../components/Cards/ItemCard";
import {
  TaskStatusKey,
  taskStatusColors,
  MeetingStatusKey,
  getProjectStatusColor,
} from "../utils/statusColors";

import { toServerUtcIso } from "../utils/time";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "../utils/draftStorage";

type StatusKey = "all" | TaskStatusKey | MeetingStatusKey;

interface LocalAttachment {
  attachment: Attachment;
  file?: File;
}

export const useProjectTaskDetail = () => {
  const { id, projectId } = useParams<{ id: string; projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const queryParams = new URLSearchParams(location.search);
  const groupIdFromUrl = queryParams.get("groupId");
  const parentIdFromUrl = queryParams.get("parentId");

  const draftKey = buildDraftKey(
    "projectTask",
    projectId || "unknown",
    groupIdFromUrl || "none",
    parentIdFromUrl || "root",
  );

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultUserName = user?.name || t("tasks.currentUser");

  const [task, setTask] = useState<ProjectTaskDetailType | null>(null);
  const [isEditing, setIsEditing] = useState(id === "new");
  const [project, setProject] = useState<any>(null);
  const [form, setForm] = useState<Partial<ProjectTaskDetailType>>({
    status: "Not Started",
    assignedUserId: user?.id || "",
    assignedUserName: defaultUserName,
    name: "",
    description: "",
    dateStart: null,
    dateEnd: null,
  });
  const [subtasks, setSubtasks] = useState<ProjectTask[]>([]);
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [draftPromptOpen, setDraftPromptOpen] = useState(false);
  const [draftToRestore, setDraftToRestore] =
    useState<Partial<ProjectTaskDetailType> | null>(null);
  const draftPromptShownRef = useRef(false);

  const statusTranslations: Record<string, string> = {
    "Not Started": t("statuses.task.not_started"),
    Started: t("statuses.task.started"),
    Completed: t("statuses.task.completed"),
    Deferred: t("statuses.task.deferred"),
    Сanceled: t("statuses.task.canceled"),
  };

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const projectData = await getProjectById(projectId);
      setProject(projectData);
    } catch (err: any) {
      console.error("Failed to load project:", err);
    }
  }, [projectId]);

  const initNewForm = useCallback(() => {
    setForm({
      status: "Not Started",
      assignedUserId: user?.id || "",
      assignedUserName: defaultUserName,
      name: "",
      description: "",
      dateStart: null,
      dateEnd: null,
      parentTaskId: parentIdFromUrl || null,
    });
    setIsEditing(true);
  }, [defaultUserName, parentIdFromUrl, user?.id]);

  const loadAll = useCallback(async () => {
    if (!project) {
      await loadProject();
    }

    if (!id || id === "new") {
      return;
    }

    setIsLoading(true);
    try {
      const taskData = await getProjectTaskById(id);
      setTask(taskData);
      setIsEditing(false);

      setForm({
        status: taskData.status || "Not Started",
        assignedUserId: taskData.assignedUserId || "",
        assignedUserName: taskData.assignedUserName || defaultUserName,
        dateStart: taskData.dateStart || null,
        dateEnd: taskData.dateEnd || null,
        description: taskData.description || "",
        name: taskData.name,
        parentTaskId: taskData.parentTaskId || null,
      });

      const st = await getProjectTaskSubtasks(id);
      setSubtasks(st.list || []);

      const attachmentsFromTask =
        taskData.attachmentsIds?.map((aid) => ({
          attachment: {
            id: aid,
            name: taskData.attachmentsNames?.[aid] || "",
            url: `/Attachment/${aid}`,
            type: taskData.attachmentsTypes?.[aid] || "",
            parentType: "ProjectTask",
            role: "Attachment",
            createdById: taskData.createdById,
          },
        })) || [];
      setAttachments(attachmentsFromTask);
    } catch (err: any) {
      console.error("Failed to load project task:", err);
      setError(t("projects.detail.loadError") || "Не удалось загрузить задачу");
    } finally {
      setIsLoading(false);
    }
  }, [id, defaultUserName, t, loadProject, project]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!projectId) return;
      try {
        const membersData = await getProjectMembers(projectId);

        const membersAsUsers: User[] = (membersData.list || []).map(
          (member: ProjectMember) => ({
            id: member.id,
            name: member.name,
            userName: member.userName || member.name,
            emailAddress: member.emailAddress || undefined,
            isActive: member.isActive,
            type: member.type || "User",
            emailAddressIsOptedOut: member.emailAddressIsOptedOut || false,
            emailAddressIsInvalid: member.emailAddressIsInvalid || false,
          }),
        );

        setUsers(membersAsUsers);
      } catch (err) {
        console.error("Failed to load project members:", err);
      }
    };

    loadUsers();
    loadAll();
  }, [id, projectId, loadAll]);

  useEffect(() => {
    draftPromptShownRef.current = false;
    setDraftPromptOpen(false);
    setDraftToRestore(null);
  }, [id, projectId, groupIdFromUrl, parentIdFromUrl]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptShownRef.current) return;
    const run = async () => {
      const draft = await loadDraft<Partial<ProjectTaskDetailType>>(draftKey);
      if (!draft?.data) return;

      const hasContent =
        !!draft.data.name ||
        !!draft.data.description ||
        !!draft.data.dateStart ||
        !!draft.data.dateEnd ||
        (draft.data.status && draft.data.status !== "Not Started");

      if (!hasContent) {
        await clearDraft(draftKey);
        return;
      }

      setDraftToRestore(draft.data);
      setDraftPromptOpen(true);
      draftPromptShownRef.current = true;
    };
    void run();
  }, [id, draftKey]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptOpen) return;
    const shouldSave =
      !!form.name ||
      !!form.description ||
      !!form.dateStart ||
      !!form.dateEnd ||
      (form.status && form.status !== "Not Started");

    if (!shouldSave) {
      clearDraft(draftKey);
      return;
    }

    saveDraft(draftKey, form);
  }, [form, id, draftKey, draftPromptOpen]);

  const handleUseDraft = () => {
    if (!draftToRestore) return;
    setForm((prev) => ({
      ...prev,
      ...draftToRestore,
      assignedUserId:
        draftToRestore.assignedUserId || prev.assignedUserId || user?.id,
      assignedUserName:
        draftToRestore.assignedUserName || prev.assignedUserName,
      parentTaskId: draftToRestore.parentTaskId || parentIdFromUrl || null,
    }));
    setIsEditing(true);
    setDraftPromptOpen(false);
  };

  const handleStartNew = () => {
    clearDraft(draftKey);
    localStorage.removeItem("pendingAttachments");
    initNewForm();
    setDraftPromptOpen(false);
  };

  const handleTextChange =
    (field: keyof Partial<Task>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const projectField = field === "taskParentId" ? "parentTaskId" : field;
      setForm((prev) => ({ ...prev, [projectField]: e.target.value }) as any);
    };

  const handleSelectChange =
    (field: keyof Partial<Task>) => (e: SelectChangeEvent<string>) => {
      const newValue = e.target.value;

      if (field === "assignedUserId") {
        const selectedUser = users.find((u) => u.id === newValue);
        setForm((prev) => ({
          ...prev,
          assignedUserId: newValue,
          assignedUserName: selectedUser?.name || defaultUserName,
        }));
        return;
      }

      const projectField = field === "taskParentId" ? "parentTaskId" : field;
      setForm(
        (prev) =>
          ({
            ...prev,
            [projectField]: newValue,
          }) as any,
      );
    };

  const handleDateChange =
    (field: keyof Partial<Task>) => (newValue: Dayjs | null) => {
      const projectField = field === "taskParentId" ? "parentTaskId" : field;
      setForm(
        (prev) =>
          ({
            ...prev,
            [projectField]: toServerUtcIso(newValue),
          }) as any,
      );
    };

  const handleSave = async () => {
    if (!projectId) {
      setError(t("tasks.detail.saveError") || "Ошибка при сохранении");
      return;
    }

    if (!project) {
      try {
        const projectData = await getProjectById(projectId);
        setProject(projectData);
      } catch (err: any) {
        setError(
          t("tasks.detail.saveError") ||
            "Ошибка при сохранении: проект не найден",
        );
        return;
      }
    }

    setError(null);
    setSuccess(null);
    setIsLoading(true);
    try {
      if (id === "new") {
        if (!form.name?.trim()) {
          setError(
            t("projects.detail.requiredFields") || "Название обязательно",
          );
          setIsLoading(false);
          return;
        }

        const selectedUser = users.find((u) => u.id === form.assignedUserId);
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const ownerId = user?.id || null;

        const parentTaskId = form.parentTaskId || parentIdFromUrl || null;
        let parentTaskName = null;

        if (parentTaskId) {
          try {
            const parentTask = await getProjectTaskById(parentTaskId);
            parentTaskName = parentTask.name || "";
          } catch (err) {
            console.error("Failed to load parent task:", err);
          }
        }

        const taskData: any = {
          projectId: project.id,
          projectName: project.name,
          name: form.name.trim(),
          description: form.description?.trim() || null,
          type: "Task",
          status: form.status || "Not Started",
          priority: "Normal",
          assignedUserId: form.assignedUserId || null,
          assignedUserName: selectedUser?.name || null,
          owner: ownerId,
          ownerId: ownerId,
          dateStart: form.dateStart || null,
          dateEnd: form.dateEnd || null,
          hours: null,
          columnId: project.firstColumnId,
          groupId: groupIdFromUrl || null,
          enableCollaborators: project.enableCollaborators || false,
          enableHours: project.enableHours || true,
          enablePriority: project.enablePriority || true,
          enableTaskDependency: project.enableTaskDependency || true,
          enablePoints: project.enablePoints || false,
          attachmentsIds: [],
          collaboratorsIds: form.assignedUserId ? [form.assignedUserId] : [],
          collaboratorsNames:
            form.assignedUserId && selectedUser
              ? { [form.assignedUserId]: selectedUser.name }
              : {},
          fromTasksIds: [],
          fromTasksNames: {},
          fromTasksColumns: {},
        };

        if (parentTaskId && parentTaskName) {
          taskData.parentTaskId = parentTaskId;
          taskData.parentTaskName = parentTaskName;
        }

        const created = await createProjectTask(taskData);
        setSuccess(
          parentTaskId
            ? t("tasks.detail.createSubtaskSuccess") || "Подзадача создана"
            : t("tasks.detail.createSuccess") || "Задача создана",
        );
        setIsEditing(false);
        navigate(`/project/${projectId}/task/${created.id}`);
        clearDraft(draftKey);
      } else {
        const updateData: any = {
          name: form.name?.trim() || "",
          description: form.description?.trim() || null,
          status: form.status || "Not Started",
          assignedUserId: form.assignedUserId || null,
          assignedUserName: form.assignedUserName || null,
          dateStart: form.dateStart || null,
          dateEnd: form.dateEnd || null,
        };

        if (form.parentTaskId && task) {
          updateData.parentTaskId = form.parentTaskId;
          updateData.parentTaskName = task.parentTaskName || "";

          if (!task.parentTaskName && form.parentTaskId) {
            try {
              const parentTask = await getProjectTaskById(form.parentTaskId);
              updateData.parentTaskName = parentTask.name || "";
            } catch (err) {
              console.error("Failed to load parent task:", err);
            }
          }
        }

        if (!id) {
          setError(t("tasks.detail.saveError") || "Ошибка при сохранении");
          setIsLoading(false);
          return;
        }
        updateData.projectId = projectId;
        await updateProjectTask(id, updateData);
        setIsEditing(false);
        setSuccess(t("tasks.detail.updateSuccess") || "Задача обновлена");
        await loadAll();
      }
    } catch (error: any) {
      console.error("Ошибка при сохранении задачи:", error);
      setError(
        error?.response?.data?.message ||
          t("tasks.detail.saveError") ||
          "Ошибка при сохранении",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      navigate(`/project/${projectId}/tasks`);
      setSuccess(t("tasks.detail.deleteSuccess") || "Задача удалена");
    } catch {
      setError(t("tasks.detail.deleteError") || "Ошибка при удалении");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (id === "new") {
      navigate(`/project/${projectId}/tasks`);
    } else {
      setIsEditing(false);
      loadAll();
    }
  };

  const handleRefresh = async () => {
    await loadAll();
  };

  const setNotification = (type: "success" | "error", message: string) => {
    if (type === "success") {
      setSuccess(message);
      setError(null);
    } else {
      setError(message);
      setSuccess(null);
    }
  };

  const mappedSubtasks: ItemWithSource[] = subtasks.map((tsk) => ({
    id: tsk.id,
    name: tsk.name,
    assignedUserName: tsk.assignedUserName || defaultUserName,
    status: (tsk.status || "Not Started") as TaskStatusKey,
    dateStart: tsk.dateStart || "",
    dateEnd: tsk.dateEnd || "",
    description: "",
    source: "task" as const,
  }));

  const getStatusConfig = (status: StatusKey, source: "task" | "meeting") => {
    if (source === "task") {
      const key = status as TaskStatusKey;
      const projectColor = getProjectStatusColor(status as string);
      return {
        color: taskStatusColors[key]?.color || projectColor,
        translation: statusTranslations[key] || status,
      };
    } else {
      return {
        color: "#000000",
        translation: status,
      };
    }
  };

  const taskForForm: Task | null =
    id === "new"
      ? null
      : task
        ? ({
            id: task.id,
            name: task.name,
            status: task.status || "Not Started",
            assignedUserId: task.assignedUserId || "",
            assignedUserName: task.assignedUserName || "",
            dateStart: task.dateStart || "",
            dateEnd: task.dateEnd || "",
            description: task.description || "",
            taskParentId: task.parentTaskId || undefined,
          } as Task)
        : null;

  const formForTaskForm: Partial<Task> = {
    ...form,
    taskParentId: form.parentTaskId || undefined,
  } as Partial<Task>;

  return {
    project,
    projectTask: task,
    task: taskForForm,
    isEditing,
    setIsEditing,
    form: formForTaskForm,
    users,
    attachments,
    mappedSubtasks,
    anchorEl,
    setAnchorEl,
    handleTextChange,
    handleSelectChange,
    handleDateChange,
    handleSave,
    handleDelete,
    handleCancel,
    handleOpenMenu: (event: React.MouseEvent<HTMLElement>) =>
      setAnchorEl(event.currentTarget),
    handleCloseMenu: () => setAnchorEl(null),
    handleRefresh,
    getStatusConfig,
    id,
    error,
    setError,
    success,
    setSuccess,
    isLoading,
    setNotification,
    draftPromptOpen,
    handleUseDraft,
    handleStartNew,
  };
};
